import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { T, F, th as palette, I, Btn } from "@/lib/bricks";
import { workspaceKind } from "@/lib/workspace";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Page, PageHeader, Stats, StatusTabs, List, Row, Empty, TextLink, Loading, FieldLabel, humanize } from "@/components/calm/Calm";
import FieldPlacer from "@/components/esign/FieldPlacer";
import EnvelopeAudit from "@/components/esign/EnvelopeAudit";
import { envelopeCountLabel } from "@/components/esign/envelopeCopy";


function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year:'numeric', month:'short', day:'numeric' });
}

const EXPIRY_OPTIONS = [['', 'No deadline'], ['3', '3 days'], ['7', '7 days'], ['14', '14 days'], ['30', '30 days']];

export default function ESignPage() {
  const { resolvedTheme } = useTheme();
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const kind = workspaceKind(user);
  const billingHref = kind === 'solo' ? '/solo/billing' : kind === 'team_lead' || kind === 'team_member' ? '/team/billing' : '/billing';
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planGate, setPlanGate] = useState(null);
  const [selectedEnv, setSelectedEnv] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [step, setStep] = useState(1);
  const [envForm, setEnvForm] = useState({ title: '', message: '', transaction_id: '', expires_in_days: '7' });
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [recipients, setRecipients] = useState([{ name: '', email: '', role: 'Signer' }]);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('All');
  const [documentFile, setDocumentFile] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fields, setFields] = useState([]);
  const [fieldForm, setFieldForm] = useState({ label: '', type: 'signature', recipient_email: '' });

  const resetWizard = () => {
    setStep(1);
    setEnvForm({ title: '', message: '', transaction_id: '', expires_in_days: '7' });
    setRecipients([{ name: '', email: '', role: 'Signer' }]);
    setDocumentFile(null);
    setDocumentId(null);
    setFields([]);
    setFieldForm({ label: '', type: 'signature', recipient_email: '' });
  };

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const load = () => {
    fetch(`${BASE}/api/esign/envelopes`, { headers })
      .then(async (r) => {
        const d = await r.json().catch(() => null);
        if (r.status === 403) {
          const detail = typeof d?.detail === 'string' ? d.detail : 'E-Signatures require the Keystone plan or higher. Upgrade in Billing to unlock.';
          setPlanGate(detail);
          setEnvelopes([]);
        } else {
          setPlanGate(null);
          setEnvelopes(Array.isArray(d) ? d : []);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
    fetch(`${BASE}/api/transactions`, { headers }).then(r => r.json()).then(d => setTransactions(Array.isArray(d) ? d : [])).catch(() => {});
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleUploadDocument = async (file) => {
    if (!file) return;
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      return toast.error("Only PDF documents are supported");
    }
    setDocumentFile(file);
    setUploading(true);
    try {
      const res = await fetch(`${BASE}/api/documents/upload-url`, {
        method: 'POST', headers,
        body: JSON.stringify({ filename: file.name, content_type: file.type || 'application/pdf', file_size: file.size, transaction_id: envForm.transaction_id || null, for_esign: true }),
      });
      if (!res.ok) throw new Error();
      const { document_id, upload_url } = await res.json();
      const putRes = await fetch(upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/pdf' } });
      if (!putRes.ok) throw new Error();
      setDocumentId(document_id);
      toast.success("Document uploaded");
    } catch {
      toast.error("Failed to upload document");
      setDocumentFile(null);
    } finally {
      setUploading(false);
    }
  };

  const apiError = async (res, fallback) => {
    const body = await res.json().catch(() => ({}));
    const d = body.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d.message === 'string') return d.message;
    return fallback;
  };

  const handleCreateEnvelope = async () => {
    if (!envForm.title) return toast.error("Title required");
    if (recipients.some(r => !r.name || !r.email)) return toast.error("All recipients require name and email");
    try {
      const res = await fetch(`${BASE}/api/esign/envelopes`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...envForm, expires_in_days: envForm.expires_in_days ? Number(envForm.expires_in_days) : null, document_id: documentId, fields, recipients }),
      });
      if (!res.ok) throw new Error(await apiError(res, "Failed to create envelope"));
      const env = await res.json();
      toast.success("Envelope created");
      setShowNew(false);
      resetWizard();
      load();
      return env;
    } catch (err) { toast.error(err.message || "Failed to create envelope"); }
  };

  const handleSend = async (envId) => {
    try {
      const res = await fetch(`${BASE}/api/esign/envelopes/${envId}/send`, { method: 'POST', headers });
      const body = await res.json().catch(() => ({}));
      const d = body.detail;
      if (!res.ok) {
        const msg = typeof d === 'string' ? d : (d && d.message) || "Could not send envelope.";
        const links = (d && d.signing_links) || body.signing_links || [];
        toast.error(msg);
        if (links[0]?.signing_url) {
          try {
            await navigator.clipboard.writeText(links[0].signing_url);
            toast.message("First signing link copied — share it manually.");
          } catch { /* ignore */ }
        }
        load();
        return;
      }
      if (body.emailed) toast.success("Envelope emailed for signing");
      else toast.message(body.hint || "Signing links ready — copy and share (email not sent).");
      setSelectedEnv(envId);
      load();
    } catch { toast.error("Could not send envelope"); }
  };

  const handleFileSignedRecord = async (envId) => {
    try {
      const res = await fetch(`${BASE}/api/esign/envelopes/${envId}/signed-record`, { method: 'POST', headers });
      if (!res.ok) throw new Error(await apiError(res, "Could not save the signed record yet."));
      const rec = await res.json().catch(() => ({}));
      toast.success(rec.location === 'deal' ? "Signed record filed in the deal's documents" : "Signed record saved in Documents");
      load();
    } catch (err) { toast.error(err.message || "Could not save the signed record yet."); }
  };

  const handleRemind = async (envId) => {
    try {
      const res = await fetch(`${BASE}/api/esign/envelopes/${envId}/remind`, { method: 'POST', headers });
      if (!res.ok) throw new Error(await apiError(res, "Could not send a reminder."));
      const body = await res.json().catch(() => ({}));
      if (body.emailed) toast.success(`Reminder sent to ${(body.reminded || []).join(', ')}`);
      else {
        const link = body.signing_links?.[0]?.signing_url;
        if (link) { try { await navigator.clipboard.writeText(link); } catch { /* ignore */ } }
        toast.message((body.hint || "Reminder email not sent.") + (link ? " Signing link copied." : ""));
      }
      load();
    } catch (err) { toast.error(err.message || "Could not send a reminder."); }
  };

  const handleVoid = async () => {
    if (!voidTarget) return;
    try {
      const res = await fetch(`${BASE}/api/esign/envelopes/${voidTarget}/void`, { method: 'POST', headers, body: JSON.stringify({ reason: voidReason.trim() || null }) });
      if (!res.ok) throw new Error(await apiError(res, "Could not void the envelope."));
      toast.success("Envelope voided. Its signing links no longer work.");
      setVoidTarget(null); setVoidReason('');
      load();
    } catch (err) { toast.error(err.message || "Could not void the envelope."); }
  };

  const addRecipient = () => setRecipients([...recipients, { name: '', email: '', role: 'Signer' }]);
  const removeRecipient = (i) => setRecipients(recipients.filter((_, idx) => idx !== i));
  const updateRecipient = (i, field, val) => setRecipients(recipients.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  // With a PDF attached, fields are placed on the document (step 4). Without one they are a signing checklist.
  const hasDoc = !!(documentFile && documentId);
  const totalSteps = hasDoc ? 4 : 3;
  const validRecipients = recipients.filter(r => r.name && r.email);
  const addField = () => {
    if (!fieldForm.label || !fieldForm.recipient_email) return toast.error("Field label and recipient required");
    setFields([...fields, { ...fieldForm, page: 1, x: 0.1, y: 0.1, placed: false, required: true }]);
    setFieldForm({ label: '', type: 'signature', recipient_email: '' });
  };
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));

  const TABS = ['All', 'Draft', 'Sent', 'Completed', 'Declined', 'Expired', 'Voided'];
  const filtered = envelopes.filter(e => {
    if (activeTab === 'All') return true;
    return e.status?.toLowerCase() === activeTab.toLowerCase();
  });

  if (loading) return <Loading />;

  const count = (st) => envelopes.filter(e => e.status === st).length;
  const reminderLocked = (env) => !!(env.next_reminder_at && new Date(env.next_reminder_at) > new Date());
  const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, color: p.ink2, fontFamily: F.display, textDecoration: 'underline', textUnderlineOffset: 3, textDecorationColor: p.hairS };
  const waitingOn = (env) => (env.recipients || []).filter(r => r.status !== 'signed').map(r => r.name || r.email);
  const nextLine = (env) => {
    if (env.status === 'draft') return 'Ready to send';
    if (env.status === 'sent') {
      const w = waitingOn(env);
      return [w.length ? `Waiting on ${w.join(', ')}` : 'Waiting on signers', env.expires_at ? { text: `sign by ${fmtDate(env.expires_at)}` } : null];
    }
    if (env.status === 'completed') return env.signed_record_filed ? 'Signed record filed' : 'Signed record not filed yet';
    if (env.status === 'declined' && env.declined) return `Declined by ${env.declined.name || env.declined.email}`;
    return null;
  };

  return (
    <Page testid="esign-page" width={960}>
      <PageHeader title="E-signatures"
        subtitle={planGate ? 'Signing comes with Keystone and above' : envelopeCountLabel(envelopes.length, count('completed'))}
        action={!planGate ? <Btn variant="primary" dark={dark} onClick={() => setShowNew(true)}>New envelope</Btn> : null} />

      {planGate ? (
        <Empty testid="esign-plan-gate" action={<Btn variant="accent" dark={dark} onClick={() => navigate(billingHref)}>Open billing</Btn>}>
          <div style={{ fontSize: 16, fontWeight: 500, color: p.ink, marginBottom: 6 }}>E-sign needs Keystone</div>
          {planGate}
        </Empty>
      ) : (<>
        <Stats items={[
          { label: 'Waiting on signers', value: count('sent') },
          { label: 'Completed', value: count('completed') },
          { label: 'Drafts', value: count('draft') },
        ]} />

        <StatusTabs label="Envelope status" options={TABS.map(t => ({ key: t, label: t }))} value={activeTab} onChange={setActiveTab} testid="esign-tabs" />

        {filtered.length === 0 ? (
          <Empty action={activeTab === 'All' ? <TextLink onClick={() => setShowNew(true)}>Create your first envelope</TextLink> : null}>
            {activeTab === 'All' ? 'No envelopes yet.' : `No ${activeTab.toLowerCase()} envelopes.`}
          </Empty>
        ) : (
          <List testid="esign-list">
            {filtered.map(env => {
              const open = selectedEnv === env.id;
              return (
                <div key={env.id}>
                  <Row testid={`envelope-${env.id}`}
                    onClick={() => setSelectedEnv(open ? null : env.id)}
                    title={env.title}
                    meta={[env.transaction_address, `${env.recipient_count || 0} recipient${env.recipient_count !== 1 ? 's' : ''}`, { text: fmtDate(env.created_at), figure: true }]}
                    next={nextLine(env)}
                    status={humanize(env.status || 'draft')}
                    attention={env.status === 'declined' || env.status === 'expired'}
                    aside={env.status === 'draft' ? (
                      <button onClick={e => { e.stopPropagation(); handleSend(env.id); }} style={{ ...linkBtn, color: p.ink }}>Send</button>
                    ) : env.status === 'sent' && env.provider !== 'docusign' ? (
                      <button onClick={e => { e.stopPropagation(); handleRemind(env.id); }} disabled={reminderLocked(env)}
                        title={reminderLocked(env) ? `Next reminder after ${new Date(env.next_reminder_at).toLocaleString('en-CA')}` : 'Email the person it is waiting on'}
                        style={{ ...linkBtn, opacity: reminderLocked(env) ? 0.45 : 1 }}>Remind</button>
                    ) : null} />
                  {open && (
                    <div data-testid={`envelope-detail-${env.id}`} style={{ padding: '4px 4px 18px', borderBottom: `1px solid ${p.hair}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {(env.recipients || []).map((r, ri) => (
                        <div key={ri} style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', fontSize: 13 }}>
                          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                            <span style={{ fontWeight: 500 }}>{r.name}</span>
                            <span style={{ color: p.ink3 }}>{` · ${r.role || 'Signer'} · `}{r.email}</span>
                          </div>
                          <span style={{ color: r.status === 'declined' ? T.brickDeep : p.ink2 }}>
                            {r.signed_at ? <>Signed <span style={{ fontFamily: F.mono, fontSize: 12 }}>{fmtDate(r.signed_at)}</span></> : humanize(r.status || 'pending')}
                          </span>
                          {r.signing_url && ['draft', 'sent'].includes(env.status) && r.status !== 'signed' && (
                            <button onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(r.signing_url).then(() => toast.success("Signing link copied")).catch(() => toast.error("Could not copy")); }}
                              style={linkBtn}>Copy link</button>
                          )}
                        </div>
                      ))}
                      {(!env.recipients || env.recipients.length === 0) && <div style={{ fontSize: 13, color: p.ink3 }}>No recipients added.</div>}
                      {env.provider !== 'docusign' && (env.declined || env.voided || (env.last_reminded_at && env.status === 'sent') || (env.expires_at && env.status === 'expired')) && (
                        <div style={{ fontSize: 12.5, color: p.ink3, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {env.expires_at && env.status === 'expired' && <div>Expired {fmtDate(env.expires_at)}</div>}
                          {env.declined && <div>Declined by {env.declined.name || env.declined.email} on {fmtDate(env.declined.at)}{env.declined.reason ? `: "${env.declined.reason}"` : ', no reason given'}</div>}
                          {env.voided && <div>Voided by {env.voided.by_name || 'your office'} on {fmtDate(env.voided.at)}{env.voided.reason ? `: "${env.voided.reason}"` : ''}</div>}
                          {env.last_reminded_at && env.status === 'sent' && <div>{`Reminded ${env.reminder_count > 1 ? `${env.reminder_count} times, last on ` : 'on '}${fmtDate(env.last_reminded_at)}`}</div>}
                        </div>
                      )}
                      {env.provider !== 'docusign' && env.status === 'completed' && (
                        <div style={{ fontSize: 12.5, color: p.ink3 }}>
                          {env.signed_record_filed
                            ? `${env.signed_record_kind === 'native_document' ? `Signed PDF (the document with ${env.signed_fields_stamped || 0} field${env.signed_fields_stamped === 1 ? '' : 's'} stamped, then the signing record)` : 'Signed record (typed signatures, times, IPs)'} is in ${env.signed_record_location === 'deal' ? "the deal's documents" : 'Documents'}${env.signed_document_name ? `: ${env.signed_document_name}` : ''}`
                            : <button onClick={e => { e.stopPropagation(); handleFileSignedRecord(env.id); }} style={linkBtn}>File signed record</button>}
                        </div>
                      )}
                      {env.provider !== 'docusign' && <EnvelopeAudit env={env} p={p} />}
                      {env.provider !== 'docusign' && ['draft', 'sent'].includes(env.status) && (
                        <div>
                          <button onClick={e => { e.stopPropagation(); setVoidTarget(env.id); setVoidReason(''); }}
                            style={{ ...linkBtn, fontSize: 12.5, color: p.ink3 }}>Void envelope</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </List>
        )}
      </>)}

      {/* New Envelope Dialog */}
      <Dialog open={showNew} onOpenChange={v => { if (!v) { setShowNew(false); resetWizard(); } }}>
        <DialogContent style={{ maxWidth: step === 4 ? 860 : 520, width: step === 4 ? '96vw' : undefined, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)', maxHeight: '90vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>
              New envelope, step {step} of {totalSteps}
            </DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              {step === 1 ? 'Set the details and link a deal.' : step === 2 ? 'Attach the document to be signed.' : step === 3 ? (hasDoc ? 'Add the people who sign, in signing order.' : 'Add recipients and assign signature fields.') : 'Place each signer\'s fields on the document.'}
            </DialogDescription>
          </DialogHeader>

          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
              <div><FieldLabel>Envelope title</FieldLabel>
                <input type="text" value={envForm.title} onChange={e => setEnvForm({ ...envForm, title: e.target.value })} placeholder="e.g. Purchase agreement, 123 Maple St"
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', boxSizing: 'border-box' }}/>
              </div>
              <div><FieldLabel>Message to recipients</FieldLabel>
                <textarea value={envForm.message} onChange={e => setEnvForm({ ...envForm, message: e.target.value })} rows={3} placeholder="Please review and sign the attached document."
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}/>
              </div>
              <div><FieldLabel>Link to a deal (optional)</FieldLabel>
                <select value={envForm.transaction_id} onChange={e => setEnvForm({ ...envForm, transaction_id: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  <option value="">No deal</option>
                  {transactions.map(t => <option key={t.id} value={t.id}>{t.property_address}, {t.deal_type}</option>)}
                </select>
              </div>
              <div><FieldLabel>Signing deadline</FieldLabel>
                <select value={envForm.expires_in_days} onChange={e => setEnvForm({ ...envForm, expires_in_days: e.target.value })}
                  style={{ marginTop: 8, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  {EXPIRY_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </select>
                <div style={{ fontSize: 12, color: p.ink3, marginTop: 6 }}>Signing links stop working after this. You can send reminders until then.</div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 20 }}>
              <div style={{ border: `1px dashed ${p.hairS}`, borderRadius: 2, padding: 'var(--bx-pad)', textAlign: 'center' }}>
                <input type="file" accept="application/pdf" id="esign-doc-upload" style={{ display: 'none' }}
                  onChange={e => handleUploadDocument(e.target.files?.[0])}/>
                <label htmlFor="esign-doc-upload" style={{ cursor: 'pointer', color: p.ink, fontFamily: F.display, fontSize: 13, fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                  {documentFile ? documentFile.name : 'Choose a PDF'}
                </label>
                {uploading && <div style={{ marginTop: 10, fontSize: 12, color: p.ink3 }}>Uploading…</div>}
                {documentId && !uploading && <div style={{ marginTop: 10, fontSize: 12, color: p.ink2 }}>Uploaded</div>}
              </div>
              <div style={{ fontSize: 12, color: p.ink3 }}>Optional. Without a document, recipients have nothing to review before signing.</div>
            </div>
          )}

          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 20 }}>
              {recipients.map((r, i) => (
                <div key={i} style={{ padding: '14px 16px', border: `1px solid ${p.hair}`, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FieldLabel>Recipient {i + 1}</FieldLabel>
                    {recipients.length > 1 && (
                      <button onClick={() => removeRecipient(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.ink3, padding: 4 }}><I name="x" size={14} color={p.ink3}/></button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <input type="text" value={r.name} onChange={e => updateRecipient(i, 'name', e.target.value)} placeholder="Full name"
                      style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none' }}/>
                    <input type="email" value={r.email} onChange={e => updateRecipient(i, 'email', e.target.value)} placeholder="Email address"
                      style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none' }}/>
                  </div>
                  <select value={r.role} onChange={e => updateRecipient(i, 'role', e.target.value)}
                    style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 13, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                    {['Signer','Buyer','Seller','Witness','Agent','Solicitor'].map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              ))}
              <button onClick={addRecipient} style={{ background: 'none', border: `1px dashed ${p.hairS}`, borderRadius: 2, padding: '10px', cursor: 'pointer', color: p.ink3, fontFamily: F.display, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                Add recipient
              </button>

              {!hasDoc && (<>
              <div style={{ marginTop: 8 }}><FieldLabel>Signature fields</FieldLabel></div>
              <div style={{ fontSize: 12, color: p.ink3, marginTop: -4 }}>
                No PDF attached, so fields appear as a signing checklist. Attach a PDF in step 2 to place fields on the document.
              </div>
              {fields.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {fields.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: `1px solid ${p.hair}`, borderRadius: 2, fontSize: 12 }}>
                      <span style={{ fontWeight: 500, flex: 1 }}>{f.label}</span>
                      <span style={{ color: p.ink3 }}>{humanize(f.type)}</span>
                      <span style={{ color: p.ink3 }}>{f.recipient_email}</span>
                      <button onClick={() => removeField(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: p.ink3, padding: 2 }}><I name="x" size={12} color={p.ink3}/></button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1.5fr auto', gap: 8, alignItems: 'center' }}>
                <input type="text" value={fieldForm.label} onChange={e => setFieldForm({ ...fieldForm, label: e.target.value })} placeholder="Field label"
                  style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 12, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none' }}/>
                <select value={fieldForm.type} onChange={e => setFieldForm({ ...fieldForm, type: e.target.value })}
                  style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 12, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  {['signature','initial','date','text'].map(v => <option key={v} value={v}>{humanize(v)}</option>)}
                </select>
                <select value={fieldForm.recipient_email} onChange={e => setFieldForm({ ...fieldForm, recipient_email: e.target.value })}
                  style={{ padding: '8px 10px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 12, fontFamily: F.display, color: p.ink, background: p.bg, outline: 'none' }}>
                  <option value="">Recipient…</option>
                  {recipients.filter(r => r.email).map((r, i) => <option key={i} value={r.email}>{r.name || r.email}</option>)}
                </select>
                <button onClick={addField} style={{ background: p.ink, color: p.bg, border: 'none', borderRadius: 2, padding: '8px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>Add</button>
              </div>
              </>)}
            </div>
          )}

          {step === 4 && hasDoc && (
            <div style={{ paddingTop: 16 }}>
              <FieldPlacer file={documentFile} recipients={validRecipients} fields={fields} onChange={setFields} p={p} />
            </div>
          )}

          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            {step === 1 && (
              <>
                <Btn variant="ghost" dark={dark} onClick={() => setShowNew(false)}>Cancel</Btn>
                <Btn variant="primary" dark={dark} onClick={() => { if (!envForm.title) return toast.error("Title required"); setStep(2); }}>Next: document</Btn>
              </>
            )}
            {step === 2 && (
              <>
                <Btn variant="ghost" dark={dark} onClick={() => setStep(1)}>Back</Btn>
                <Btn variant="primary" dark={dark} onClick={() => { if (uploading) return toast.error("Wait for upload to finish"); setStep(3); }}>Next: recipients</Btn>
              </>
            )}
            {step === 3 && (
              <>
                <Btn variant="ghost" dark={dark} onClick={() => setStep(2)}>Back</Btn>
                {hasDoc
                  ? <Btn variant="primary" dark={dark} onClick={() => {
                      if (recipients.some(r => !r.name || !r.email)) return toast.error("All recipients require name and email");
                      setFields(fields.filter(f => f.placed && recipients.some(r => r.email === f.recipient_email)));
                      setStep(4);
                    }}>Next: place fields</Btn>
                  : <Btn variant="primary" dark={dark} onClick={handleCreateEnvelope}>Create envelope</Btn>}
              </>
            )}
            {step === 4 && (
              <>
                <Btn variant="ghost" dark={dark} onClick={() => setStep(3)}>Back</Btn>
                <Btn variant="primary" dark={dark} onClick={() => {
                  const unsigned = validRecipients.filter(r => !fields.some(f => f.placed && f.recipient_email === r.email && (f.type === 'signature' || f.type === 'initial')));
                  if (unsigned.length) return toast.error(`Place a signature or initials field for ${unsigned.map(r => r.name).join(', ')}`);
                  handleCreateEnvelope();
                }}>Create envelope</Btn>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!voidTarget} onOpenChange={v => { if (!v) { setVoidTarget(null); setVoidReason(''); } }}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Void this envelope?</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Every signing link stops working right away. Signatures already collected stay on record. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <textarea value={voidReason} onChange={e => setVoidReason(e.target.value)} rows={3} maxLength={500} placeholder="Reason (optional, kept on the envelope)"
            style={{ marginTop: 16, width: '100%', padding: '10px 12px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: 'transparent', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}/>
          <DialogFooter style={{ marginTop: 20, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => { setVoidTarget(null); setVoidReason(''); }}>Keep it</Btn>
            <Btn variant="primary" dark={dark} onClick={handleVoid}>Void envelope</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
